import { useUserStore } from '@rendora/common/store';
import { isUS } from '@rendora/common/utils/config';
import { getPlatformJumpLink } from '@rendora/common/utils/url';
import { debounce } from 'lodash-es';
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import type { IOrder, ISubscription } from '../../../entities/order';
import {
  postOrderClient,
  postOrderClientBuyCreditPackage,
  postOrderClientBuyStorage,
  postOrderClientBuySubAccount,
  postOrderClientSubscriptionCreate,
} from '../../../entities/order';
import {
  BUY_TYPE,
  IRightsType,
  ORDER_BEHAVIOR,
  ORDER_STATUS,
  ORDER_TYPE,
  PAY_TYPE,
} from '../../../types/enums';
import type { UseOrderExpose, UseOrderInput } from './types';

/**
 * useOverseasOrder
 * 海外订单创建的可组合函数。
 * - 对外 API：参数与返回结构稳定。
 * - 功能：根据订单类型与支付方式创建订单、设置二维码。
 */
export default function useOverseasOrder(
  orderBody: Readonly<ReturnType<typeof ref<Partial<UseOrderInput>>>>,
): UseOrderExpose {
  const userStore = useUserStore();

  // 中断控制器：用于在重新创建订单时取消进行中的请求
  const abort = ref<AbortController>();

  // 基础状态
  const isCreating = ref(false);
  const raw = ref<IOrder | ISubscription>();
  const agreementAccepted = ref(isUS() ? true : false);
  const currentPayType = ref<PAY_TYPE>(PAY_TYPE.STRIPE);
  const currentRightType = ref<IRightsType>(orderBody.value?.rightsType ?? IRightsType.PERSONAL);

  // 二维码地址
  const qrCode = reactive({
    weixin: '',
    alipay: '',
  });

  // 订单视图状态
  const order = reactive<{ id?: number; status?: ORDER_STATUS }>({});
  const paySucceeded = computed(() => order.status === ORDER_STATUS.PAID);

  // 工具：按照当前支付方式设置二维码
  function setQrcode(url: string): void {
    if (currentPayType.value === PAY_TYPE.WEIXIN) qrCode.weixin = url;
    if (currentPayType.value === PAY_TYPE.ALIPAY) qrCode.alipay = url;
  }

  // 重新支付：清空状态并重新创建订单
  function repay(): void {
    abandon();
    void create();
  }

  // 创建订单
  async function create(): Promise<void> {
    abandon();

    abort.value = new AbortController();
    isCreating.value = true;

    // 协议未勾选时不允许创建
    if (!agreementAccepted.value) {
      isCreating.value = false;
      return;
    }

    try {
      const body = orderBody.value as Partial<UseOrderInput>;
      if (!body) {
        isCreating.value = false;
        return;
      }

      const chosenPayType = body.payType ?? currentPayType.value;

      let res:
        | Awaited<ReturnType<typeof postOrderClientBuyCreditPackage>>
        | Awaited<ReturnType<typeof postOrderClientBuyStorage>>
        | Awaited<ReturnType<typeof postOrderClientBuySubAccount>>
        | Awaited<ReturnType<typeof postOrderClientSubscriptionCreate>>
        | Awaited<ReturnType<typeof postOrderClient>>;

      const overseaBaseParams = {
        email: userStore.user?.email_address || '',
        success_url: getPlatformJumpLink(`/paymentResult`),
        cancel_url: getPlatformJumpLink(`${location.pathname}`),
      };

      // 点数（信用币）
      if (body.orderType === ORDER_TYPE.CREDITS) {
        if (!chosenPayType || !body.creditCoinsPackageId) {
          isCreating.value = false;
          return;
        }
        res = await postOrderClientBuyCreditPackage(
          {
            ...overseaBaseParams,
            order_type: ORDER_TYPE.CREDITS,
            pay_type: chosenPayType,
            credit_coins_package_id: body.creditCoinsPackageId,
            packageInfo: body.packageInfo,
          },
          { signal: abort.value.signal },
        );
        // 存储
      } else if (body.orderType === ORDER_TYPE.STORAGE) {
        const isGB = body.isGB ?? body.currentPackageType === 'GB';
        if (!chosenPayType || typeof body.num !== 'number') {
          isCreating.value = false;
          return;
        }
        res = await postOrderClientBuyStorage(
          {
            ...overseaBaseParams,
            order_type: ORDER_TYPE.STORAGE,
            currentPackage: body.currentPackage,
            pay_type: chosenPayType,
            num: body.num,
            is_GB: Boolean(isGB),
          },
          { signal: abort.value.signal },
        );
        // 加购子账号（席位）
      } else if (body.orderType === ORDER_TYPE.ACCOUNT) {
        if (!chosenPayType || typeof body.num !== 'number') {
          isCreating.value = false;
          return;
        }
        res = await postOrderClientBuySubAccount(
          {
            ...overseaBaseParams,
            pay_type: chosenPayType,
            num: body.num,
          },
          { signal: abort.value.signal },
        );
        // 订阅
      } else if (body.buyType === BUY_TYPE.SUBSCRIBE) {
        if (!body.rightsId) {
          isCreating.value = false;
          return;
        }
        res = await postOrderClientSubscriptionCreate(
          {
            ...overseaBaseParams,
            rights_id: Number(body.rightsId),
            behavior: body.behavior ?? ORDER_BEHAVIOR.BUY,
          },
          { signal: abort.value.signal },
        );
        // 套餐类订单（一次性权益购买）
      } else {
        if (
          body.rightsType === undefined ||
          body.behavior === undefined ||
          !body.timeIdentity ||
          !chosenPayType
        ) {
          isCreating.value = false;
          return;
        }
        res = await postOrderClient(
          {
            ...overseaBaseParams,
            pay_type: chosenPayType,
            rights_type: body.rightsType,
            behavior: body.behavior,
            time_identity: body.timeIdentity,
          },
          { signal: abort.value.signal },
        );
      }

      const data = res.data as IOrder | ISubscription | undefined;

      if (!data) {
        isCreating.value = false;
        return;
      }

      raw.value = data;
      order.id = data.id;

      const paymentUrl =
        (data as ISubscription).subscription_url ||
        (data as IOrder).qr_code_url ||
        (data as IOrder).vendor_redirect_url;

      if (paymentUrl) setQrcode(paymentUrl);
    } finally {
      isCreating.value = false;
    }
  }

  // 更换支付方式：清空二维码并通过重新创建订单实现
  function changePayType(next: PAY_TYPE): void {
    abandon();
    currentPayType.value = next;
    void create();
  }

  // 别名：对外仅暴露 setPayType，内部复用 changePayType
  function setPayType(next: PAY_TYPE): void {
    changePayType(next);
  }

  function abandon() {
    if (abort.value) abort.value.abort();
    order.status = undefined;
    raw.value = undefined;
    qrCode.weixin = '';
    qrCode.alipay = '';
  }

  // 防抖，避免频繁切换支付方式触发多次创建
  const debounceChangePayType = debounce((next: PAY_TYPE) => changePayType(next), 500);

  // 支付方式切换时：非订阅场景触发重新创建
  watch(
    () => currentPayType.value,
    next => {
      const body = orderBody.value as Partial<UseOrderInput> | undefined;
      if (body?.buyType !== BUY_TYPE.SUBSCRIBE) {
        isCreating.value = true;
        debounceChangePayType(next);
      }
    },
  );

  // 入参变更时：重置二维码并重新创建
  watch(
    () => orderBody.value,
    () => {
      void create();
    },
  );

  // 协议勾选状态变化：勾选后创建订单
  watch(
    () => agreementAccepted.value,
    val => {
      if (val) void create();
    },
  );

  // 组件卸载时清理资源
  onBeforeUnmount(() => {
    if (abort.value) abort.value.abort();
  });

  return {
    agreementAccepted,
    currentPayType,
    currentRightType,
    qrCode,
    order,
    raw,
    paySucceeded,
    isCreating,
    abandon,
    repay,
    create,
    setPayType,
  };
}
